import React, { useState } from 'react';
import { Lock, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { resetPassword } from '../services/authApi';
import { describeError } from '../utils/describeError';
import { AllodosLogo } from './AllodosLogo';

/**
 * Bloco 39: tela aberta pelo link do e-mail de recuperação. O usuário cria e
 * confirma a nova senha; não há entrada automática na conta pelo link.
 */
export const ResetPassword: React.FC<{ token: string; onDone: () => void; onRequestNew: () => void }> = ({ token, onDone, onRequestNew }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) return setError('A nova senha precisa ter pelo menos 8 caracteres.');
    if (password !== confirm) return setError('As senhas não conferem.');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  const expired = !!error && /inválido|expirado/i.test(error);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 font-sans">
      <div className="w-full max-w-md bg-slate-900/80 border border-white/5 rounded-[2.5rem] p-5 sm:p-8 md:p-10 shadow-2xl space-y-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-40"><AllodosLogo variant="white" /></div>
          <h1 className="text-xl font-black text-white tracking-tight">Redefinir senha</h1>
          <p className="text-xs text-slate-400 font-medium">Crie uma nova senha para a sua conta. A senha anterior deixa de valer.</p>
        </div>

        {done ? (
          <div className="space-y-6 text-center">
            <div className="flex items-center justify-center gap-3 p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm font-bold">
              <CheckCircle2 size={20} /> Senha redefinida com sucesso.
            </div>
            <button type="button" onClick={onDone} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs">
              Ir para o login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            {error && (
              <div className="flex gap-3 p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-300 text-xs font-bold">
                <AlertCircle size={18} className="shrink-0" />
                <div>
                  {error}
                  {expired && (
                    <button type="button" onClick={onRequestNew} className="block mt-2 underline text-rose-200">
                      Solicitar um novo link de recuperação
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Nova senha</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input type="password" required minLength={8} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mín. 8 caracteres" className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Confirmar nova senha</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                <input type="password" required minLength={8} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repita a senha" className="w-full bg-slate-900/60 border border-white/5 rounded-2xl p-4 pl-12 text-sm text-white focus:ring-4 focus:ring-indigo-500/10 focus:outline-none" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-600/50 text-white py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-xs flex items-center justify-center gap-3">
              {loading ? <Loader2 className="animate-spin" size={18} /> : 'Salvar nova senha'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
